import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, get_office_app_data, get_office_schema, patch_office_app_data } from '../../api';
import { useApi } from '../../use_api';
import { useAuth } from '../../auth/AuthProvider';
import { SchemaForm, ReadonlyWidget, type FieldNode } from '@captomi/schema-form';
import { GreenZonesWidget } from './GreenZonesWidget';
import { ChipsWidget } from './ChipsWidget';
import { schema_for_trade } from './profile-schema';

// F3 — the customer edits their own business profile via the shared @captomi/schema-form renderer.
// The BFF emits the audience-filtered schema and enforces field-level editability; this view owns
// the data layer (load / diff / patch) and the customer-curated widget policy:
//   - editable only on `tenant` leaves (admin/never are read-only, marked 🔒);
//   - object-arrays / raw-JSON / overrides render READ-ONLY (no raw editors for customers) via the
//     fallback + a couple of overrides — preserving the console's pre-extraction behavior.
// Members (P16) get the whole form disabled.

type Obj = Record<string, unknown>;

function value_at(data: Obj, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Obj)[k] : undefined), data);
}
function set_path(obj: Obj, path: string, value: unknown): void {
  const ks = path.split('.');
  let o = obj;
  for (let i = 0; i < ks.length - 1; i++) {
    if (typeof o[ks[i]!] !== 'object' || o[ks[i]!] === null) o[ks[i]!] = {};
    o = o[ks[i]!] as Obj;
  }
  o[ks[ks.length - 1]!] = value;
}

const CONSOLE_IS_EDITABLE = (n: FieldNode): boolean => n.editable === 'tenant';
const CONSOLE_WIDGETS = {
  object_array: ReadonlyWidget,
  json: ReadonlyWidget,
  'overrides-calendar': ReadonlyWidget,
  // Editable weekly-hours editor (was read-only JSON) — green_zones is editable:'tenant'.
  'green-zones': GreenZonesWidget,
  // Chips editor that splits commas/newlines into separate entries (service areas, specialties).
  chips: ChipsWidget,
};

export function ProfileView() {
  const { state } = useAuth();
  const read_only = state.status === 'signed_in' && state.session.role === 'member';
  const schema = useApi(() => get_office_schema(), []);
  const data = useApi(() => get_office_app_data(), []);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0); // bump to remount the form (clear edits/buffers) after save
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [msg, setMsg] = useState<string | null>(null);

  // Deep-link target from Get Started (`?focus=<field label>`): once the form has rendered, scroll
  // to that field, outline it briefly, and focus its control. Matched by the field's rendered label
  // (the schema's `.meta({ label })`); `startsWith` tolerates a trailing editable/locked marker.
  const [params] = useSearchParams();
  const focus = params.get('focus');
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focus || schema.loading || data.loading) return;
    const timer = window.setTimeout(() => {
      const root = formRef.current;
      if (!root) return;
      const want = focus.trim().toLowerCase();
      const label = Array.from(root.querySelectorAll('label')).find((l) =>
        (l.textContent ?? '').trim().toLowerCase().startsWith(want),
      );
      const field = label?.parentElement;
      if (!field) return;
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.style.outline = '2px solid var(--accent)';
      field.style.outlineOffset = '4px';
      field.style.borderRadius = '8px';
      (field.querySelector('input, select, textarea') as HTMLElement | null)?.focus({ preventScroll: true });
      window.setTimeout(() => {
        field.style.outline = '';
        field.style.outlineOffset = '';
      }, 2600);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [focus, schema.loading, data.loading, version]);

  if (schema.loading || data.loading) return <p className="muted">Loading…</p>;
  if (schema.error || !schema.data) return <p className="error">Couldn’t load the form.</p>;
  if (data.error) return <p className="error">Couldn’t load your data.</p>;

  const d = (data.data?.data ?? {}) as Obj;
  // Hide painter-only top-level profile fields (service areas, specialties, …) for SaaS tenants.
  const form_schema = schema_for_trade(schema.data.schema, value_at(d, 'trade.primary_category'));
  const get_value = (path: string): unknown => (path in edits ? edits[path] : value_at(d, path));
  const set_value = (path: string, v: unknown): void => setEdits((e) => ({ ...e, [path]: v }));
  const dirty = Object.keys(edits).length > 0;

  async function save() {
    setSaving(true);
    setMsg(null);
    const patch: Obj = {};
    for (const [path, v] of Object.entries(edits)) set_path(patch, path, v);
    try {
      await patch_office_app_data(patch);
      setEdits({});
      data.reload();
      setVersion((v) => v + 1);
      setMsg('Saved.');
    } catch (e) {
      const reason = e instanceof ApiError ? e.reason : '';
      setMsg(
        reason === 'field_not_editable'
          ? 'Some of those fields can’t be edited.'
          : reason === 'invalid_value'
            ? 'Some values aren’t valid — please fix the highlighted fields and try again.'
            : 'Save failed. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Business profile</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {read_only
          ? 'You have view-only access. Ask an owner or admin to make changes.'
          : 'Edit your business details. Fields marked 🔒 are managed by Captomi.'}
      </p>
      <div key={version} ref={formRef} style={{ maxWidth: 640 }}>
        {/* A disabled fieldset natively disables every control inside it (members, P16). */}
        <fieldset disabled={read_only} style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}>
          <SchemaForm
            schema={form_schema}
            getValue={get_value}
            setValue={set_value}
            widgets={CONSOLE_WIDGETS}
            fallbackWidget={ReadonlyWidget}
            isEditable={CONSOLE_IS_EDITABLE}
            lockedMarker="🔒"
          />
        </fieldset>
        {!read_only && (
          <button className="btn" disabled={!dirty || saving} onClick={() => void save()} style={{ marginTop: 12 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>
      {msg && <p className="muted" style={{ marginBottom: 0 }}>{msg}</p>}
    </div>
  );
}
